import { memo } from "react";
import { Copy, FileText, Globe } from "lucide-react";
import CvTemplatePicker from "@/components/CvTemplatePicker";
import RichText from "@/components/RichText";

// Cheap regex pre-built once at module load — avoids JIT'ing the same pattern
// every render during a stream that may fire 30-60 setState calls per second.
const ROUTE_SF_RE = /\/app\/salesforce/i;
const ROUTE_TH_RE = /\/app\/trailhead/i;
const BLOCKQUOTE_RE = /^>\s+(.+?)(?:\n\n|$)/ms;
// Trigger phrases the model emits (per _temporal_grounding) when it lacks
// live data. Multilingual: EN/TR/ES/FR/DE/IT cover ~99% of our users.
const NEEDS_WEB_RE = /(don't have live data|don't have real-time|🌐\s*web search toggle|web search toggle|canlı veri|gerçek zamanlı veri|gercek zamanli veri|datos en tiempo real|données en temps réel|echtzeitdaten|dati in tempo reale)/i;
const CV_START_TAG = "<<<CV_START>>>";
const CV_END_TAG = "<<<CV_END>>>";

function extractCvAndClean(raw) {
  if (!raw || !raw.includes(CV_START_TAG) || !raw.includes(CV_END_TAG)) {
    return { cvMd: null, cleaned: raw || "" };
  }
  const sIdx = raw.indexOf(CV_START_TAG);
  const eIdx = raw.indexOf(CV_END_TAG);
  const cvMd = raw.slice(sIdx + CV_START_TAG.length, eIdx).trim();
  const cleaned = (raw.slice(0, sIdx) + raw.slice(eIdx + CV_END_TAG.length)).trim();
  return { cvMd, cleaned };
}

function ChatMessageBubble({
  m,
  t,
  cvTemplateId,
  handleCvTemplateChange,
  downloadCVAsPDF,
  copyMsg,
  routeButtonPrompt,
  webSearchActive,
  onEnableWebSearch,
}) {
  const { cvMd, cleaned } = extractCvAndClean(m.content);
  const isUser = m.role === "user";
  const isAssistant = m.role === "assistant";
  // Route buttons only show on FINISHED assistant messages (not during stream)
  // because content is still partial mid-stream — early detection would render
  // a button for a URL that never gets fully written.
  const finished = isAssistant && !m._streaming;
  const text = cleaned || "";
  const hasSf = finished && ROUTE_SF_RE.test(text);
  const hasTh = finished && ROUTE_TH_RE.test(text);
  // "Enable Web Search & Retry" CTA: only when the AI explicitly punted on
  // freshness AND we're not already in web mode AND we have a prior user
  // query to re-run with.
  const needsWeb =
    finished &&
    !webSearchActive &&
    !!routeButtonPrompt &&
    !!onEnableWebSearch &&
    NEEDS_WEB_RE.test(text);

  let suggested = "";
  if (hasSf || hasTh) {
    const bq = text.match(BLOCKQUOTE_RE);
    if (bq) suggested = bq[1].replace(/\n>\s*/g, "\n").trim();
    if (!suggested) suggested = (routeButtonPrompt || "").replace(/^\[image attached\] /, "");
  }
  const sfHref = `/app/salesforce${suggested ? "?prompt=" + encodeURIComponent(suggested.slice(0, 2000)) : ""}`;
  const thHref = `/app/trailhead${suggested ? "?text=" + encodeURIComponent(suggested.slice(0, 2000)) : ""}`;

  return (
    <div className={`flex chat-msg-enter ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
      <div className={`max-w-[85%] p-4 rounded-xl text-base leading-relaxed whitespace-pre-wrap font-medium chat-bubble-rich ${
        isUser
          ? "bg-zinc-950 text-white"
          : "bg-white border-l-4 border-l-orange-500 border-y border-r border-zinc-200 text-zinc-900 group relative shadow-sm"
      }`}>
        {m.preview_image && (
          <img src={m.preview_image} alt="attached" className="rounded-md max-h-56 mb-2 border border-white/20" />
        )}
        {m.generated_image_b64 && (
          <div className="mb-2">
            <img
              src={`data:image/png;base64,${m.generated_image_b64}`}
              alt="generated"
              className="rounded-md max-w-full border border-zinc-300"
              data-testid="chat-generated-image"
            />
            <a
              href={`data:image/png;base64,${m.generated_image_b64}`}
              download={`lumioos-${m.id.slice(0, 8)}.png`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
              data-testid="chat-image-download"
            >
              ⬇ {t("chat_download_image") || "Download"}
            </a>
          </div>
        )}
        {cvMd && (
          <div className="mb-3 bg-white border border-blue-200 rounded-lg p-4 not-prose" data-testid="chat-cv-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-full">
                <FileText className="w-3 h-3" /> {t("chat_cv_ready") || "Your CV is ready"}
              </div>
              <button
                onClick={() => downloadCVAsPDF(cvMd, m.id)}
                data-testid={`chat-cv-download-${m.id}`}
                className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
              >
                <FileText className="w-3 h-3" /> {t("chat_cv_download") || "Open print preview"}
              </button>
            </div>
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Template</div>
              <CvTemplatePicker value={cvTemplateId} onChange={handleCvTemplateChange} />
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded p-3 max-h-72 overflow-y-auto text-xs font-mono whitespace-pre-wrap leading-relaxed">{cvMd}</div>
          </div>
        )}
        <RichText>{cleaned.replace(/^\[image attached\] /, "")}</RichText>
        {(hasSf || hasTh) && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="chat-route-buttons">
            {hasSf && (
              <a href={sfHref} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-xs font-bold no-underline" style={{ background: "linear-gradient(135deg,#00A1E0,#003e6b)" }} data-testid="chat-route-sf-btn">
                🚀 Salesforce Agent&apos;ı Aç
              </a>
            )}
            {hasTh && (
              <a href={thHref} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-xs font-bold no-underline" style={{ background: "linear-gradient(135deg,#0a8b46,#053f1f)" }} data-testid="chat-route-th-btn">
                📚 Trailhead Solver&apos;ı Aç
              </a>
            )}
          </div>
        )}
        {needsWeb && (
          <div className="mt-3" data-testid="chat-enable-web-cta">
            <button
              type="button"
              onClick={() => onEnableWebSearch(routeButtonPrompt)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-xs font-bold bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors"
              data-testid="chat-enable-web-btn"
            >
              <Globe className="w-3.5 h-3.5" />
              {t("chat_enable_web_retry") || "Enable Web Search & retry"}
            </button>
          </div>
        )}
        {/* Streaming UX: 3-dot "AI is thinking" indicator while we wait for
            the very first token; once content arrives, swap to the inline
            blinking cursor so the user can see the message growing live. */}
        {m._streaming && !cleaned && (
          <div className="chat-typing-dots" data-testid="chat-typing-dots" aria-label="Lumioo is thinking">
            <span /><span /><span />
          </div>
        )}
        {m._streaming && cleaned && (
          <span
            className="inline-block w-[2px] h-4 ml-0.5 bg-zinc-900 align-middle"
            style={{ animation: "blink 0.8s steps(2, end) infinite" }}
            aria-hidden="true"
          />
        )}
        {finished && (
          <button onClick={() => copyMsg(m.content)}
            className="absolute -top-2 -right-2 bg-white border border-zinc-200 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:border-blue-600"
            aria-label={t("chat_copy")} data-testid="chat-copy-msg">
            <Copy className="w-3 h-3 text-zinc-600" />
          </button>
        )}
      </div>
    </div>
  );
}

// Memo with a precise equality check: a message bubble only re-renders when
// its OWN content / streaming state changes. During a stream, all the
// non-streaming bubbles get to skip their re-render entirely, killing the
// "jank" caused by inline CV/regex extraction running 30-60x per second.
export default memo(ChatMessageBubble, (prev, next) => {
  const a = prev.m;
  const b = next.m;
  return (
    a.id === b.id &&
    a.content === b.content &&
    a._streaming === b._streaming &&
    a.preview_image === b.preview_image &&
    a.generated_image_b64 === b.generated_image_b64 &&
    prev.cvTemplateId === next.cvTemplateId &&
    prev.webSearchActive === next.webSearchActive &&
    prev.routeButtonPrompt === next.routeButtonPrompt
  );
});
